<template>
    <div ref="root" class="topbar-period">
        <div class="tp-trigger" :class="{ open }" @click="open = !open">
            <svg
                class="tp-icon"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                aria-hidden="true"
            >
                <rect x="3" y="4" width="18" height="18" rx="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <div class="tp-text">
                <span class="tp-preset-name">{{ activeLabel }}</span>
                <span class="tp-range-val">{{ activeRangeText }}</span>
            </div>
            <svg
                class="tp-chevron"
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                aria-hidden="true"
            >
                <polyline points="6 9 12 15 18 9" />
            </svg>
        </div>

        <div class="tp-dropdown" :class="{ open }">
            <div class="tp-group-label">{{ t("components.common.appTopBar.quickSelect") }}</div>
            <div
                v-for="preset in presets"
                :key="preset.code"
                class="tp-option"
                :class="{ active: activeCode === preset.code }"
                @click="applyPreset(preset.code)"
            >
                {{ preset.name }}
                <span class="tp-opt-range">{{ rangeText(presetRange(preset.code)) }}</span>
            </div>

            <div class="tp-divider"></div>

            <div class="tp-group-label">{{ t("components.common.appTopBar.customRange") }}</div>
            <div class="tp-custom">
                <div class="tp-custom-grid">
                    <div class="tp-date-grp">
                        <label for="tp-from">{{ t("components.common.appTopBar.from") }}</label>
                        <input id="tp-from" v-model="customFrom" type="date" :max="maxIso" />
                    </div>
                    <div class="tp-date-grp">
                        <label for="tp-to">{{ t("components.common.appTopBar.to") }}</label>
                        <input id="tp-to" v-model="customTo" type="date" :max="maxIso" />
                    </div>
                </div>
                <button class="tp-apply" type="button" @click="applyCustom">
                    {{ t("components.common.appTopBar.applyCustomRange") }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import useHelpers from "@/composables/helpers";
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import { useAppStore } from "../../store/app";
import { useI18n } from "vue-i18n";

const toIso = (date) => {
    const month = `${date.getMonth() + 1}`.padStart(2, "0");
    const day = `${date.getDate()}`.padStart(2, "0");

    return `${date.getFullYear()}-${month}-${day}`;
};

export default {
    name: "BmPeriodPicker",
    setup() {
        const { t } = useI18n();
        const appStore = useAppStore();
        const { calendarRange } = storeToRefs(appStore);
        const { fetchTableDataByReport, formatDate } = useHelpers();

        const root = ref(null);
        const open = ref(false);
        const activeCode = ref("mtd");
        const customFrom = ref("");
        const customTo = ref("");
        const maxIso = toIso(new Date());

        // The six presets from bm-dashboard, in its order, preceded by the three
        // day ranges the old picker had — design review asked to keep those.
        const presets = computed(() => [
            { code: "l7d", name: t("components.common.appTopBar.dateRangeOptions.l7d") },
            { code: "l30d", name: t("components.common.appTopBar.dateRangeOptions.l30d") },
            { code: "l90d", name: t("components.common.appTopBar.dateRangeOptions.l90d") },
            { code: "mtd", name: t("components.common.appTopBar.dateRangeOptions.mtd") },
            { code: "lm", name: t("components.common.appTopBar.dateRangeOptions.lm") },
            { code: "cq", name: t("components.common.appTopBar.dateRangeOptions.cq") },
            { code: "pq", name: t("components.common.appTopBar.dateRangeOptions.pq") },
            { code: "ytd", name: t("components.common.appTopBar.dateRangeOptions.ytd") },
            { code: "ly", name: t("components.common.appTopBar.dateRangeOptions.ly") },
        ]);

        const presetRange = (code) => {
            const today = new Date();
            const year = today.getFullYear();
            const month = today.getMonth();
            const day = today.getDate();
            const quarterFirstMonth = Math.floor(month / 3) * 3;

            // Offsets copied verbatim from the pre-redesign picker so the numbers
            // users see don't shift. Note they are inconsistent: l7d covers 7 days
            // inclusive, while l30d/l90d actually cover 31 and 91.
            if (code === "l7d") {
                return [new Date(year, month, day - 6), new Date(year, month, day)];
            }

            if (code === "l30d") {
                return [new Date(year, month, day - 30), new Date(year, month, day)];
            }

            if (code === "l90d") {
                return [new Date(year, month, day - 90), new Date(year, month, day)];
            }

            if (code === "mtd") {
                return [new Date(year, month, 1), new Date(year, month, day)];
            }

            if (code === "lm") {
                return [new Date(year, month - 1, 1), new Date(year, month, 0)];
            }

            if (code === "cq") {
                return [new Date(year, quarterFirstMonth, 1), new Date(year, month, day)];
            }

            if (code === "pq") {
                const from = new Date(year, quarterFirstMonth - 3, 1);

                return [from, new Date(from.getFullYear(), from.getMonth() + 3, 0)];
            }

            if (code === "ytd") {
                return [new Date(year, 0, 1), new Date(year, month, day)];
            }

            if (code === "ly") {
                return [new Date(year - 1, 0, 1), new Date(year - 1, 11, 31)];
            }

            return null;
        };

        const rangeText = (range) =>
            range && range[0] && range[1] ? `${formatDate(range[0])} — ${formatDate(range[1])}` : "";

        const isSameDay = (a, b) =>
            a &&
            b &&
            a.getFullYear() === b.getFullYear() &&
            a.getMonth() === b.getMonth() &&
            a.getDate() === b.getDate();

        // The store keeps the range, not which preset produced it, so on mount we
        // work out whether it still matches one — otherwise the label would read
        // "custom" every time the user navigates between pages.
        const matchStoredPreset = () => {
            const [from, to] = calendarRange.value;
            const match = presets.value.find((preset) => {
                const range = presetRange(preset.code);

                return isSameDay(range[0], from) && isSameDay(range[1], to);
            });

            return match ? match.code : "custom";
        };

        const syncCustomInputs = () => {
            if (calendarRange.value[0]) {
                customFrom.value = toIso(calendarRange.value[0]);
            }

            if (calendarRange.value[1]) {
                customTo.value = toIso(calendarRange.value[1]);
            }
        };

        const commit = (range) => {
            calendarRange.value = range;
            syncCustomInputs();
            open.value = false;
            fetchTableDataByReport();
        };

        const applyPreset = (code) => {
            activeCode.value = code;
            commit(presetRange(code));
        };

        const applyCustom = () => {
            if (!customFrom.value || !customTo.value) {
                return;
            }

            let from = new Date(`${customFrom.value}T00:00:00`);
            let to = new Date(`${customTo.value}T00:00:00`);

            if (from > to) {
                [from, to] = [to, from];
            }

            activeCode.value = "custom";
            commit([from, to]);
        };

        const activeLabel = computed(() => {
            if (activeCode.value === "custom") {
                return t("components.common.appTopBar.dateRangeOptions.custom");
            }

            return presets.value.find((preset) => preset.code === activeCode.value)?.name ?? "";
        });

        const activeRangeText = computed(() => rangeText(calendarRange.value));

        const onDocumentClick = (event) => {
            if (open.value && root.value && !root.value.contains(event.target)) {
                open.value = false;
            }
        };

        onMounted(() => {
            // Preserves the previous behaviour: default to month-to-date on a fresh
            // session, otherwise keep whatever range the store already holds.
            if (!calendarRange.value[0] || !calendarRange.value[1]) {
                const range = presetRange("mtd");
                calendarRange.value[0] = range[0];
                calendarRange.value[1] = range[1];
                activeCode.value = "mtd";
            } else {
                activeCode.value = matchStoredPreset();
            }

            syncCustomInputs();
            document.addEventListener("click", onDocumentClick);
        });

        onBeforeUnmount(() => {
            document.removeEventListener("click", onDocumentClick);
        });

        return {
            activeCode,
            activeLabel,
            activeRangeText,
            applyCustom,
            applyPreset,
            customFrom,
            customTo,
            maxIso,
            open,
            presetRange,
            presets,
            rangeText,
            root,
            t,
        };
    },
};
</script>
